import { useAppDrawer } from '../../components/AppDrawer/AppDrawerProvider';
import { FC, useEffect, useRef, useState } from 'react';
import InvestigationDrawer from '../../components/InvestigationDrawer/InvestigationDrawer';
import { QueryFilters, useConfiguration, useLastSync } from './api';
import Filters from './components/Filters/Filters';
import {
    Alert,
    BarChart,
    Button,
    ButtonStyles,
    Icon,
    Icons,
    LayoutCarousel,
    SingleMetric,
    Tooltip
} from '@silverfort/sf-components';
import useUniversalMfaMetrics from './hooks/useUniversalMfaMetrics';
import PolicyTypeChart from './components/PolicyTypeChart/PolicyTypeChart';
import { PolicyCountByStatus } from './types';
import MfaResponseChart from './components/MfaResponseChart/MfaResponseChart';
import MfaPolicyCarousel from './components/MfaPolicyCarousel/MfaPolicyCarousel';
import AuthenticationsTrafficLayout from './components/AuthenticationsTrafficLayout/AuthenticationsTrafficLayout';
import { useAuthenticationsTrafficBarChart } from './hooks/useAuthenticationsTrafficBarChart';
import AuthenticationTypeChart from './components/AuthenticationTypeChart/AuthenticationTypeChart';
import ProtocolTypeChart from './components/ProtocolTypeChart/ProtocolTypeChart';
import TokensDistributionChart from './components/TokensDistributionChart/TokensDistributionChart';
import TopDailyMfaChart from './components/TopDailyMfaChart/TopDailyMfaChart';
import TopEntityChart from './components/TopUnprotectedEntityChart/TopEntityChart';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import TopRiskEntityChart from '../../components/TopRiskEntityChart/TopRiskEntityChart';
import TopRiskIndicatorChart from '../../components/TopRiskIndicatorChart/TopRiskIndicatorChart';
import LogsDrawer from '../../containers/LogsDrawer/LogsDrawer';
import { LogsFilters } from '../../containers/LogsDrawer/types';
import Insights from './components/Insights/Insights';
import useUniversalMfaDashboardToolbarActions from './hooks/useUniversalMfaDashboardToolbarActions';
import { useTowerLayout } from '../../TowerLayout/TowerLayoutProvider';
import Disclaimers from './components/Disclaimers/Disclaimers';
import { formatAuthTrafficShortDates } from './utils/utils';
import styles from './UniversalMFADashboard.module.scss';
import { DashboardUtils } from '../../utils/policies';

const AUTH_TRAFFIC_LABELS_LENGTH_THRESHOLD = 30;
const EXCLUDED_CAPTURE_CLASS_NAME = 'exclude-from-capture';

const UniversalMfaDashboard: FC = () => {
    const { displayContent } = useAppDrawer();
    const { setLayoutContent } = useTowerLayout();

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const captureContainerRef = useRef<HTMLDivElement>(null);
    const captureFixedHeaderContainerRef = useRef<HTMLDivElement>(null);
    const captureScrollContainerRef = useRef<HTMLDivElement>(null);

    const [filters, setFilters] = useState<QueryFilters>({ domain: [], timeframe: '30' } as QueryFilters);
    const [chartWidth, setChartWidth] = useState(0);
    const [chartShouldBlink, setChartShouldBlink] = useState(false);
    const [mfaPolicyStatus, setMfaPolicyStatus] = useState<keyof PolicyCountByStatus>('total');
    const [bridgeMfaPolicyStatus, setBridgeMfaPolicyStatus] = useState<keyof PolicyCountByStatus>('total');

    const { data: lastSync } = useLastSync();
    const { data: configuration } = useConfiguration();

    const blinkChartContainerClassName = `transition-colors duration-200 ${chartShouldBlink ? '[&]:bg-[#F5FAFF]' : 'bg-sf-white'}`;

    const handleFiltersChange = (nextFilters: QueryFilters) => {
        if (nextFilters.timeframe !== filters.timeframe) {
            setChartShouldBlink(true);
            setTimeout(() => {
                setChartShouldBlink(false);
            }, 2000);
        }
        setFilters(nextFilters);
    };

    const handleInvestigationClick = (guid: string, adType: number) => {
        displayContent(<InvestigationDrawer guid={guid} adType={adType} />);
    };

    const handleUsersInvestigation = (guid: string) => {
        handleInvestigationClick(guid, AD_ENTITY_TYPES.USER);
    };

    const handleResourcesInvestigation = (guid: string) => {
        handleInvestigationClick(guid, AD_ENTITY_TYPES.SERVER);
    };

    const goToPolicyPage = () => {
        window.open('/Policy', '_blank');
    };

    const goToPolicyPageCreatePolicy = () => {
        window.open('/policy#create', '_blank');
    };

    const {
        selectedTab: authenticationsTrafficSelectedTab,
        setSelectedTab: setAuthenticationsTrafficSelectedTab,
        barChartData,
        isLoading: barChartIsLoading,
        barChartVariant,
        mappingList
    } = useAuthenticationsTrafficBarChart({
        filters
    });

    const {
        generateAndDownloadUniversalMfaMetricsFile,
        isGenerateAndDownloadUniversalMfaMetricsFileLoading,
        generateAndDownloadUniversalMfaCapture,
        isGenerateAndDownloadUniversalMfaCaptureLoading
    } = useUniversalMfaDashboardToolbarActions({
        filters,
        captureContainerRef,
        captureFixedHeaderContainerRef,
        captureScrollContainerRef
    });

    const {
        protectedUsers,
        protectedUsersIsLoading,
        mfaPolicies,
        mfaPoliciesIsLoading,
        policyType,
        policyTypeIsLoading,
        mfaResponse,
        mfaResponseIsLoading,
        mfaAuthentications,
        mfaAuthenticationsIsLoading,
        mfaTriggerRate,
        mfaTriggerRateIsLoading,
        medianMfa,
        medianMfaIsLoading,
        bridgeMfaPolicies,
        bridgeMfaPoliciesIsLoading,
        authenticationTypeSummary,
        authenticationTypeSummaryIsLoading,
        protocolTypeSummary,
        protocolTypeSummaryIsLoading,
        tokenDistributionSummary,
        tokenDistributionSummaryIsLoading,
        topDailyMfa = [],
        topDailyMfaIsLoading,
        activeUsers,
        activeUsersIsLoading,
        activeResources,
        activeResourcesIsLoading,
        riskyUsers = [],
        riskyUsersIsLoading,
        riskyResources = [],
        riskyResourcesIsLoading,
        riskyIndicators = [],
        riskyIndicatorsIsLoading
    } = useUniversalMfaMetrics(filters);

    const isLoading =
        protectedUsersIsLoading ||
        mfaAuthenticationsIsLoading ||
        mfaResponseIsLoading ||
        medianMfaIsLoading ||
        bridgeMfaPoliciesIsLoading ||
        authenticationTypeSummaryIsLoading ||
        protocolTypeSummaryIsLoading ||
        tokenDistributionSummaryIsLoading ||
        topDailyMfaIsLoading ||
        activeUsersIsLoading ||
        activeResourcesIsLoading ||
        riskyUsersIsLoading ||
        riskyResourcesIsLoading ||
        riskyIndicatorsIsLoading;

    const getLogsMaxEndDate = (): Date | undefined => {
        if (!lastSync) {
            return;
        }
        const lastSyncLessOneMillisecond = new Date(lastSync.getTime() - 1);
        return lastSyncLessOneMillisecond;
    };

    const openLogsDrawer = (logsFilters: LogsFilters) => {
        const logsFiltersConfig = DashboardUtils.getLogsFiltersFromDefaultAuthLogsFilters(
            configuration?.authFilters,
            logsFilters
        );

        displayContent(
            <LogsDrawer
                filters={{
                    ...logsFilters,
                    action: 'mfa',
                    domain: filters.domain,
                    timeframe: filters.timeframe,
                    ...logsFiltersConfig.filters
                }}
                excludedFilters={logsFiltersConfig.excludedFilters}
                logsTableTitle="MFA authentications"
                maxEndDate={getLogsMaxEndDate()}
            />
        );
    };

    useEffect(() => {
        const updateWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        setLayoutContent(<Disclaimers />);
    }, [setLayoutContent]);

    return (
        <>
            <div id="wrapper" className="h-[calc(100vh-64px)] flex justify-center mt-[64px] p-0 bg-sf-white">
                <div
                    ref={captureContainerRef}
                    className="flex flex-col max-w-full 2xl:max-w-[1400px] 3xl:max-w-[1800px] bg-sf-gray-50 border-s border-sf-gray-100 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <div
                        ref={captureFixedHeaderContainerRef}
                        className="w-full flex flex-row justify-between gap-6 px-6 pt-6 pb-4 bg-sf-gray-50 sticky top-0 z-10">
                        <Filters
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            excludedCaptureClassName={EXCLUDED_CAPTURE_CLASS_NAME}
                            lastSync={lastSync ?? undefined}
                        />
                        <div className={`flex flex-row gap-2 ${EXCLUDED_CAPTURE_CLASS_NAME}`}>
                            <Tooltip title="Export CSV">
                                <Button
                                    iconRight={Icons.Download}
                                    buttonStyle={ButtonStyles.Primary}
                                    title="Export CSV"
                                    disabled={isLoading}
                                    isLoading={isGenerateAndDownloadUniversalMfaMetricsFileLoading}
                                    onClick={generateAndDownloadUniversalMfaMetricsFile}
                                />
                            </Tooltip>
                            <Tooltip title="Capture">
                                <Button
                                    iconRight={Icons.Capture}
                                    buttonStyle={ButtonStyles.Primary}
                                    title="Capture"
                                    disabled={isLoading}
                                    onClick={() =>
                                        generateAndDownloadUniversalMfaCapture([EXCLUDED_CAPTURE_CLASS_NAME])
                                    }
                                    isLoading={isGenerateAndDownloadUniversalMfaCaptureLoading}
                                />
                            </Tooltip>
                            <Button
                                iconLeft={Icons.Add}
                                buttonStyle={ButtonStyles.PrimarySolid}
                                disabled={isLoading}
                                onClick={() => goToPolicyPageCreatePolicy()}
                                className='text-nowrap'
                                text='Create Policy'
                            />
                        </div>
                    </div>
                    <div ref={captureScrollContainerRef} className="flex-1 px-6 pb-6">
                        {/* CSS Grid Layout for stable chart sizing */}
                        <div className={styles.dashboardGrid}>
                            {/* MFA Policies Section */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'mfa-policies' }}>
                                <MfaPolicyCarousel
                                    title="MFA policies"
                                    policies={mfaPolicies}
                                    isLoading={mfaPoliciesIsLoading}
                                    selectedStatus={mfaPolicyStatus}
                                    onStatusChange={setMfaPolicyStatus}
                                    onIconClick={goToPolicyPage}
                                />
                            </div>

                            {/* Protected users Metric Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'protected-users' }}>
                                <SingleMetric
                                    title="Protected users"
                                    value={protectedUsers ?? 0}
                                    isLoading={protectedUsersIsLoading}
                                    iconRight={Icons.ArrowTrendUp}
                                    iconClassName="text-sf-gray-400"
                                    className={`text-21 ${blinkChartContainerClassName}`}
                                />
                            </div>

                            {/* MFA authentications Metric Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'mfa-auths' }}>
                                <SingleMetric
                                    title="MFA authentications"
                                    value={mfaAuthentications ?? 0}
                                    isLoading={mfaAuthenticationsIsLoading}
                                    iconRight={Icons.ArrowTrendUp}
                                    iconClassName="text-sf-gray-400"
                                    className={`text-21 ${blinkChartContainerClassName}`}
                                />
                            </div>

                            {/* MFA Trigger Rate Card */}
                            <div className="h-[94px] min-h-[94px]" style={{ gridArea: 'trigger-rate' }}>
                                <LayoutCarousel
                                    title="MFA Trigger Rate"
                                    className={blinkChartContainerClassName}
                                    contentClassName="py-2 flex flex-1 flex-col justify-center [&]:items-start group"
                                    pages={
                                        <div className="flex flex-row gap-2 px-2 w-full">
                                            {mfaTriggerRateIsLoading ? (
                                                <div className="h-6 w-[112px] animate-pulse rounded-sm bg-sf-gray-50"></div>
                                            ) : (
                                                <div className="flex flex-row justify-between w-full items-center text-16 font-bold">
                                                    {mfaTriggerRate?.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </div>

                            {/* Policy type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'policy-type' }}>
                                <PolicyTypeChart
                                    policyType={policyType}
                                    policyTypeIsLoading={policyTypeIsLoading}
                                    selectedStatus={mfaPolicyStatus}
                                />
                            </div>

                            {/* MFA response Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'mfa-response' }}>
                                <MfaResponseChart
                                    mfaResponse={mfaResponse}
                                    isLoading={mfaResponseIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(response) => openLogsDrawer({ mfa: response })}
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Bridge Policies and Median MFA */}
                            <div className="h-[200px] min-h-[200px] flex flex-col gap-2" style={{ gridArea: 'bridge' }}>
                                <div className="flex-1">
                                    <SingleMetric
                                        title="Median daily MFA"
                                        value={medianMfa ?? 0}
                                        isLoading={medianMfaIsLoading}
                                        iconRight={Icons.ArrowTrendUp}
                                        iconClassName="text-sf-gray-400"
                                        className={`text-21 ${blinkChartContainerClassName}`}
                                    />
                                </div>
                                <div className="flex-1">
                                    <MfaPolicyCarousel
                                        title="Bridge policies"
                                        policies={bridgeMfaPolicies}
                                        isLoading={bridgeMfaPoliciesIsLoading}
                                        selectedStatus={bridgeMfaPolicyStatus}
                                        onStatusChange={setBridgeMfaPolicyStatus}
                                        onIconClick={goToPolicyPage}
                                    />
                                </div>
                            </div>

                            {/* Authentication Traffic Chart */}
                            <div
                                className="h-[380px] min-h-[380px]"
                                style={{ gridArea: 'traffic' }}
                                ref={chartContainerRef}>
                                <AuthenticationsTrafficLayout
                                    selectedTab={authenticationsTrafficSelectedTab}
                                    className={blinkChartContainerClassName}
                                    onTabChange={(tab) => setAuthenticationsTrafficSelectedTab(tab)}>
                                    <BarChart
                                        data={barChartData}
                                        isLoading={barChartIsLoading}
                                        primaryColor="#50017A"
                                        gradientEndColor="#D6C7DD"
                                        variant={barChartVariant}
                                        width={chartWidth}
                                        XAxisTickStyle={{
                                            fontSize: 10,
                                            color: '#808080'
                                        }}
                                        YAxisTickStyle={{
                                            fontSize: 10,
                                            color: '#808080'
                                        }}
                                        mappingList={mappingList}
                                        formatXAxisTick={
                                            barChartData.length >= AUTH_TRAFFIC_LABELS_LENGTH_THRESHOLD
                                                ? formatAuthTrafficShortDates
                                                : undefined
                                        }
                                        height={252}
                                    />
                                </AuthenticationsTrafficLayout>
                            </div>

                            {/* Authentication type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'auth-type' }}>
                                <AuthenticationTypeChart
                                    authenticationTypeSummary={authenticationTypeSummary}
                                    isLoading={authenticationTypeSummaryIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(authenticationType) =>
                                        openLogsDrawer({ directoryType: [authenticationType] })
                                    }
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Protocol type Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'protocol-type' }}>
                                <ProtocolTypeChart
                                    protocolTypeSummary={protocolTypeSummary}
                                    isLoading={protocolTypeSummaryIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(protocolType) => openLogsDrawer({ protocol: protocolType })}
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Token Distribution Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'token-dist' }}>
                                <TokensDistributionChart
                                    tokenDistributionSummary={tokenDistributionSummary}
                                    isLoading={tokenDistributionSummaryIsLoading}
                                    className={blinkChartContainerClassName}
                                    onClick={(token) => openLogsDrawer({ authenticator: token })}
                                    innerCircleEmptyStateClassName={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Top Daily MFA Chart */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'daily-mfa' }}>
                                <TopDailyMfaChart
                                    title="Top daily MFA"
                                    list={topDailyMfa}
                                    isLoading={topDailyMfaIsLoading}
                                    showAvatar={true}
                                    className={blinkChartContainerClassName}
                                />
                            </div>

                            {/* Separator */}
                            <div className="w-full flex items-center" style={{ gridArea: 'separator' }}>
                                <div className="px-2 h-6 flex items-center gap-1">
                                    <Icon icon={Icons.Refresh} size={16} />
                                    <span className="font-sf text-12 font-medium text-sf-gray-700">Last 24h</span>
                                </div>
                                <div className="flex-1 border-b border-sf-gray-300" />
                            </div>

                            {/* Top Active Users */}
                            <div className="h-full min-h-[240px] w-full" style={{ gridArea: 'active-users' }}>
                                <TopEntityChart
                                    title="Top active users"
                                    filters={filters}
                                    onInvestigationClick={handleUsersInvestigation}
                                    entities={activeUsers}
                                    entitiesIsLoading={activeUsersIsLoading}
                                    entityType="users"
                                />
                            </div>

                            {/* Top Active Resources */}
                            <div className="h-full min-h-[240px] w-full" style={{ gridArea: 'active-resources' }}>
                                <TopEntityChart
                                    title="Top active resources"
                                    filters={filters}
                                    onInvestigationClick={handleResourcesInvestigation}
                                    entities={activeResources}
                                    entitiesIsLoading={activeResourcesIsLoading}
                                    entityType="resources"
                                />
                            </div>

                            {/* Top Risky Users */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-users' }}>
                                <TopRiskEntityChart
                                    title="Top risky users"
                                    list={riskyUsers}
                                    isLoading={riskyUsersIsLoading}
                                    showAvatar={true}
                                    onInvestigationClick={handleUsersInvestigation}
                                />
                            </div>

                            {/* Top Risky Resources */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-resources' }}>
                                <TopRiskEntityChart
                                    title="Top risky resources"
                                    list={riskyResources}
                                    isLoading={riskyResourcesIsLoading}
                                    onInvestigationClick={handleResourcesInvestigation}
                                />
                            </div>

                            {/* Top Risk Indicators */}
                            <div className="h-[200px] min-h-[200px]" style={{ gridArea: 'risk-indicators' }}>
                                <TopRiskIndicatorChart
                                    title="Top risk indicators"
                                    list={riskyIndicators}
                                    isLoading={riskyIndicatorsIsLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Insights />
            </div>
            <Alert />
        </>
    );
};

export default UniversalMfaDashboard;
